import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Button, Chip, IconButton, Tooltip } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import DashportLogo from "../../../assets/dashport-logos/DASHPORT_white_horizontal.png";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 20,
    paddingBottom: 100,
    minHeight: "90vh",
    backgroundColor: "#0d1b2a",
    color: "#fff",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 15,
    marginBottom: 20,
  },
  logo: {
    height: 40,
  },
  modalidadesBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 30,
  },
  slide: {
    textAlign: "center",
    padding: "20px 40px",
    maxWidth: 700,
    margin: "0 auto",
  },
  categoriaTitulo: {
    fontWeight: "bold",
    marginBottom: 30,
    color: "#ffd54f",
  },
  ganadorFila: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    padding: "14px 0",
    fontSize: 20,
    borderBottom: "1px solid rgba(255,255,255,0.15)",
  },
  ganadorNombre: {
    flexGrow: 1,
    textAlign: "left",
    marginLeft: 10,
  },
  posicion: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: 18,
    color: "#fff",
    flexShrink: 0,
  },
  navBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
    flexWrap: "wrap",
    padding: "15px 20px",
    backgroundColor: "#0d1b2a",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.5)",
    zIndex: 10,
  },
  contador: {
    fontSize: 16,
    opacity: 0.8,
  },
  finBanner: {
    marginTop: 20,
    padding: 15,
    backgroundColor: "#2e7d32",
    borderRadius: 8,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 15,
    flexWrap: "wrap",
  },
  botonBlanco: {
    color: "#fff",
    borderColor: "#fff",
  },
}));

function colorPorPosicion(posicion) {
  if (posicion === 1) return "#c9a227";
  if (posicion === 2) return "#9e9e9e";
  if (posicion === 3) return "#a05a2c";
  return "#607d8b";
}

export default function CeremoniaPremiacion({ modalidadGroups, tituloEvento }) {
  const classes = useStyles();
  const grupos = useMemo(() => modalidadGroups || [], [modalidadGroups]);

  const [invertido, setInvertido] = useState(false);

  const gruposMostrados = useMemo(
    () => (invertido ? [...grupos].reverse() : grupos),
    [grupos, invertido]
  );

  const [codModSeleccionado, setCodModSeleccionado] = useState(
    gruposMostrados.length > 0 ? gruposMostrados[0].codMod : null
  );
  const [indiceCategoria, setIndiceCategoria] = useState(0);

  const indiceModalidad = gruposMostrados.findIndex((m) => m.codMod === codModSeleccionado);
  const modalidadActual = indiceModalidad >= 0 ? gruposMostrados[indiceModalidad] : null;
  const categorias = useMemo(() => {
    const base = modalidadActual ? modalidadActual.categorias : [];
    return invertido ? [...base].reverse() : base;
  }, [modalidadActual, invertido]);
  const categoriaActual = categorias[indiceCategoria];
  const esUltima = categorias.length > 0 && indiceCategoria === categorias.length - 1;
  const hayModalidadSiguiente =
    indiceModalidad >= 0 && indiceModalidad < gruposMostrados.length - 1;

  function seleccionarModalidad(codMod) {
    setCodModSeleccionado(codMod);
    setIndiceCategoria(0);
  }

  function irSiguiente() {
    setIndiceCategoria((i) => (i < categorias.length - 1 ? i + 1 : i));
  }

  function irAnterior() {
    setIndiceCategoria((i) => (i > 0 ? i - 1 : i));
  }

  function irSiguienteModalidad() {
    if (hayModalidadSiguiente) {
      seleccionarModalidad(gruposMostrados[indiceModalidad + 1].codMod);
    }
  }

  function invertirOrden() {
    const nuevoInvertido = !invertido;
    const nuevosGrupos = nuevoInvertido ? [...grupos].reverse() : grupos;
    setInvertido(nuevoInvertido);
    if (nuevosGrupos.length > 0) {
      setCodModSeleccionado(nuevosGrupos[0].codMod);
      setIndiceCategoria(0);
    }
  }

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "ArrowRight") irSiguiente();
      if (e.key === "ArrowLeft") irAnterior();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorias.length]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <img src={DashportLogo} alt="Dashport" className={classes.logo} />
        <Typography variant="h5">{tituloEvento || "Ceremonia de premiación"}</Typography>
      </div>

      <div className={classes.modalidadesBar}>
        {gruposMostrados.map((m) => (
          <Chip
            key={m.codMod}
            label={m.descripcionModalidad}
            color={m.codMod === codModSeleccionado ? "primary" : "default"}
            onClick={() => seleccionarModalidad(m.codMod)}
            clickable
          />
        ))}
        <Tooltip title="Invertir orden de distancias y categorías">
          <IconButton
            size="small"
            onClick={invertirOrden}
            className={classes.botonBlanco}
          >
            <SwapVertIcon />
          </IconButton>
        </Tooltip>
      </div>

      {!modalidadActual && (
        <Typography variant="h6" align="center">
          Selecciona una modalidad para comenzar la premiación.
        </Typography>
      )}

      {modalidadActual && categoriaActual && (
        <div className={classes.slide}>
          <Typography variant="h4" className={classes.categoriaTitulo}>
            {categoriaActual.cat} — {categoriaActual.generoLabel}
          </Typography>

          {categoriaActual.ganadores.map((g, i) => (
            <div key={i} className={classes.ganadorFila}>
              <div
                className={classes.posicion}
                style={{ backgroundColor: colorPorPosicion(g.posicion) }}
              >
                {g.posicion}
              </div>
              <span className={classes.ganadorNombre}>
                #{g.num} — {g.nombre}
              </span>
              <span>{g.tiempo}</span>
            </div>
          ))}

          <div className={classes.navBar}>
            <Button
              variant="outlined"
              className={classes.botonBlanco}
              onClick={irAnterior}
              disabled={indiceCategoria === 0}
              startIcon={<ArrowBackIosIcon />}
            >
              Anterior
            </Button>
            <span className={classes.contador}>
              Categoría {indiceCategoria + 1} de {categorias.length}
            </span>
            {!esUltima && (
              <Button
                variant="contained"
                color="primary"
                onClick={irSiguiente}
                endIcon={<ArrowForwardIosIcon />}
              >
                Siguiente
              </Button>
            )}
          </div>

          {esUltima && (
            <div className={classes.finBanner}>
              <span>
                ✓ Última categoría de {modalidadActual.descripcionModalidad}.
              </span>
              {hayModalidadSiguiente && (
                <Button
                  variant="contained"
                  onClick={irSiguienteModalidad}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Ir a la siguiente modalidad
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
